// Minimal markdown -> React renderer, purpose-built for the docs/manuals/*.md
// files (2026-07-08, George: "where do I find the Manuals on the CRM?").
// No new dependency -- CLAUDE.md requires asking before adding libraries, and
// a full markdown lib is overkill for the plain heading/list/table/bold
// subset these manuals actually use. Handles: #/##/### headings, **bold**,
// *italic*, `code`, [text](url), - / 1. lists, | tables |, --- rules,
// blank-line paragraph breaks. Not a general-purpose parser -- if a manual's
// markdown grows more exotic, extend this rather than reaching for a library
// without asking first.
import React from 'react';

let keyCounter = 0;
const nextKey = () => `md-${keyCounter++}`;

// Inline: **bold**, *italic*, `code`, [text](url) -- mixed, non-nested-safe
// (nesting bold-in-italic etc. isn't needed by these docs).
function renderInline(text) {
  const nodes = [];
  let buf = '';
  const flush = () => { if (buf) { nodes.push(buf); buf = ''; } };
  let i = 0;
  while (i < text.length) {
    const ch = text[i], next = text[i + 1];
    if (ch === '[') {
      const closeBracket = text.indexOf(']', i + 1);
      if (closeBracket !== -1 && text[closeBracket + 1] === '(') {
        const closeParen = text.indexOf(')', closeBracket + 2);
        if (closeParen !== -1) {
          flush();
          const label = text.slice(i + 1, closeBracket);
          const url = text.slice(closeBracket + 2, closeParen);
          nodes.push(<a key={nextKey()} href={url} target="_blank" rel="noreferrer" style={{ color: '#E36F2C' }}>{label}</a>);
          i = closeParen + 1;
          continue;
        }
      }
    }
    if (ch === '*' && next === '*') {
      const end = text.indexOf('**', i + 2);
      if (end !== -1) {
        flush();
        nodes.push(<strong key={nextKey()}>{text.slice(i + 2, end)}</strong>);
        i = end + 2;
        continue;
      }
    }
    if (ch === '*' && next !== '*' && next !== ' ' && i > 0) {
      const end = text.indexOf('*', i + 1);
      if (end !== -1 && text[end - 1] !== ' ') {
        flush();
        nodes.push(<em key={nextKey()}>{text.slice(i + 1, end)}</em>);
        i = end + 1;
        continue;
      }
    }
    if (ch === '`') {
      const end = text.indexOf('`', i + 1);
      if (end !== -1) {
        flush();
        nodes.push(
          <code key={nextKey()} style={{ background: '#F3F4F6', color: '#B91C1C', padding: '1px 5px', borderRadius: 3, fontSize: '0.9em' }}>
            {text.slice(i + 1, end)}
          </code>
        );
        i = end + 1;
        continue;
      }
    }
    buf += ch;
    i++;
  }
  flush();
  return nodes;
}

const H = { fontWeight: 800, color: '#1F2937', lineHeight: 1.3 };

export default function MiniMarkdown({ source }) {
  const lines = String(source || '').split(/\r?\n/);
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.trim() === '') { i++; continue; }

    // Fenced code block
    if (/^```/.test(line.trim())) {
      const buf = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i].trim())) { buf.push(lines[i]); i++; }
      i++; // skip closing fence
      blocks.push(
        <pre key={nextKey()} style={{ background: '#1F2937', color: '#E5E7EB', padding: 14, borderRadius: 6, overflowX: 'auto', fontSize: 12.5, lineHeight: 1.5 }}>
          <code>{buf.join('\n')}</code>
        </pre>
      );
      continue;
    }

    // Horizontal rule
    if (/^---+$/.test(line.trim())) {
      blocks.push(<hr key={nextKey()} style={{ border: 'none', borderTop: '1px solid #E5E7EB', margin: '20px 0' }} />);
      i++;
      continue;
    }

    // Headings
    const h = line.match(/^(#{1,4})\s+(.*)$/);
    if (h) {
      const level = h[1].length;
      const text = h[2].trim();
      const styleByLevel = [
        { fontSize: 26, ...H, marginTop: 28, marginBottom: 12, borderBottom: '2px solid #E36F2C', paddingBottom: 8 },
        { fontSize: 21, ...H, marginTop: 24, marginBottom: 10 },
        { fontSize: 17, ...H, marginTop: 18, marginBottom: 8, color: '#E36F2C' },
        { fontSize: 14, ...H, marginTop: 14, marginBottom: 6 },
      ][level - 1];
      const Tag = `h${level}`;
      blocks.push(<Tag key={nextKey()} style={styleByLevel}>{renderInline(text)}</Tag>);
      i++;
      continue;
    }

    // Table (header row + separator row required)
    if (line.includes('|') && lines[i + 1] && /^\s*\|?[\s:|-]+\|?\s*$/.test(lines[i + 1]) && lines[i + 1].includes('-')) {
      const splitRow = (row) => row.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim());
      const header = splitRow(line);
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].includes('|') && lines[i].trim() !== '') { rows.push(splitRow(lines[i])); i++; }
      blocks.push(
        <div key={nextKey()} style={{ overflowX: 'auto', margin: '10px 0' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
            <thead>
              <tr>{header.map((c, ci) => (
                <th key={ci} style={{ textAlign: 'left', padding: '7px 10px', background: '#1F2937', color: '#fff', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  {renderInline(c)}
                </th>
              ))}</tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri} style={{ background: ri % 2 ? '#F9FAFB' : '#fff', borderBottom: '1px solid #E5E7EB' }}>
                  {r.map((c, ci) => <td key={ci} style={{ padding: '7px 10px', verticalAlign: 'top' }}>{renderInline(c)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    // Bullet list
    if (/^[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i])) { items.push(lines[i].replace(/^[-*]\s+/, '')); i++; }
      blocks.push(
        <ul key={nextKey()} style={{ margin: '6px 0', paddingLeft: 22, fontSize: 14, lineHeight: 1.6, color: '#374151' }}>
          {items.map((it, ii) => <li key={ii} style={{ marginBottom: 3 }}>{renderInline(it)}</li>)}
        </ul>
      );
      continue;
    }

    // Numbered list
    if (/^\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) { items.push(lines[i].replace(/^\d+\.\s+/, '')); i++; }
      blocks.push(
        <ol key={nextKey()} style={{ margin: '6px 0', paddingLeft: 22, fontSize: 14, lineHeight: 1.6, color: '#374151' }}>
          {items.map((it, ii) => <li key={ii} style={{ marginBottom: 3 }}>{renderInline(it)}</li>)}
        </ol>
      );
      continue;
    }

    // Blockquote (used for callouts like "> **Note:**")
    if (/^>\s?/.test(line)) {
      const buf = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) { buf.push(lines[i].replace(/^>\s?/, '')); i++; }
      blocks.push(
        <div key={nextKey()} style={{ borderLeft: '3px solid #E36F2C', background: '#FFF7ED', padding: '8px 14px', margin: '10px 0', fontSize: 13.5, color: '#374151', lineHeight: 1.6 }}>
          {buf.map((l, li) => <div key={li}>{renderInline(l)}</div>)}
        </div>
      );
      continue;
    }

    // Paragraph -- collect contiguous non-blank, non-block lines
    const buf = [line];
    i++;
    while (i < lines.length && lines[i].trim() !== '' && !/^(#{1,4}\s|[-*]\s|>\s|```|---+$|\d+\.\s)/.test(lines[i])) {
      buf.push(lines[i]);
      i++;
    }
    blocks.push(<p key={nextKey()} style={{ fontSize: 14, lineHeight: 1.65, color: '#374151', margin: '8px 0' }}>{renderInline(buf.join(' '))}</p>);
  }

  return <div>{blocks}</div>;
}
